/**
 * Every session in the World, across projects (RFC 0281 §6).
 *
 * THE INBOX. A bare `a` in the url grammar used to mean "the session list",
 * which was one project's because there was only ever one. Binding the window
 * to a World gives it the reading it should always have had: the list across
 * every project, which is what having several of them immediately demands.
 *
 * A PROJECTION, NOT A REGISTRY. RFC 0281 §4 keeps sessions registered on their
 * project rather than on the World: the World document is the one every client
 * opens first, and an append-mostly list of every session ever would be the same
 * churn that forced presence out into its own document (RFC 0261). The cost is
 * one read per project instead of one read total, which is the cheaper side of
 * the trade at the number of projects a person has, and the honest place to pay
 * it is here.
 *
 * EVERY ROW CARRIES ITS PROJECT, which is the half a single-project list never
 * needed. Opening a row has to mint a leaf naming the project that holds the
 * session, or the pane resolves against the wrong catalog and prunes itself.
 */

import {
  isSessionDone,
  type ProjectDoc,
  registryStatus,
  type SessionRunStatus,
  STATUS_FRESH_MS,
  sessionTitleOf,
} from '@mod/core/model'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useProjectDocs } from '../../shell/spaces/useProjectCatalogs'
import { useWorldProjects } from './useWorldProjects'

/** One session in the inbox: a row, plus where to open it. */
export interface WorldSessionRow {
  id: string
  title: string
  updatedAt?: number
  completedAt?: number
  /** What it is DOING, when the registry can say (RFC 0284 S8a). Absent means
   *  the registry cannot tell, which is never the same as idle. */
  status?: SessionRunStatus
  /** When that status was claimed. Lets a reader know when a run claim will
   *  age out, so it can look again then and not before. */
  statusAt?: number
  /** When a message was last sent to it (isSessionDone reads it). */
  sentAt?: number
  /** The project whose registry lists it. What a leaf opening this must name. */
  projectId: string
  /** That project's display name, for a row that has to say where it is from. */
  projectName: string
  /** The session that opened this one, when an agent did. The inbox filters on
   *  it for the same reason a project's list does: six sub-sessions fanned out
   *  by one turn are six rows nobody asked for, across every project at once. */
  parentSessionId?: string
}

/** What `worldSessionRows` reads per project: the registry, already hydrated. */
export interface WorldSessionSource {
  projectId: string
  projectName: string
  doc: ProjectDoc | undefined
}

/**
 * Flatten every project's session registry into one list, newest first.
 *
 * SORTED ACROSS PROJECTS, not within them. An inbox grouped by project would be
 * a directory with extra steps: the reason to look at every project at once is
 * to see what has moved, and that ordering is by time regardless of where the
 * work lives.
 *
 * A project whose document has not arrived contributes nothing rather than
 * throwing. It appears when it syncs, which is the same contract the sidebar's
 * directory keeps for a root with no children yet.
 *
 * Undated sessions sort last rather than first. `updatedAt` is absent on rows
 * written before it was recorded, and treating "unknown" as "just now" would put
 * the oldest sessions at the top of a list whose whole job is recency.
 *
 * BOUNDED, because this is a recency view. Without a cap the list grows with
 * every session ever created in every project, and it is rebuilt whenever ANY
 * project document changes — which is constantly, since the session registry
 * lives on that document. A year of work would re-sort a year of history to
 * render one screen, on every keystroke that touched a session.
 *
 * The real fix is not here: it is moving the session registry off the project
 * document, for the same reason RFC 0261 moved presence off the World. This
 * bounds the render cost meanwhile, and it is an honest bound — nobody scrolls
 * to session four hundred.
 *
 * Pure, so the ordering, the project stamping and the cap are testable without
 * a repo.
 */
export const WORLD_INBOX_LIMIT = 200

export function worldSessionRows(
  sources: WorldSessionSource[],
  limit: number = WORLD_INBOX_LIMIT,
  /** For the freshness rule (RFC 0284 S8a). Taken rather than read so the
   *  ordering, the stamping and the staleness stay testable without a clock. */
  now: number = Date.now(),
): WorldSessionRow[] {
  const rows: WorldSessionRow[] = []
  const seen = new Set<string>()

  for (const { projectId, projectName, doc } of sources) {
    if (!doc) continue
    const view = doc as {
      sessionIds?: string[]
      sessionMeta?: Record<
        string,
        {
          title?: string
          updatedAt?: number
          completedAt?: number
          status?: unknown
          statusAt?: unknown
          sentAt?: unknown
          parentSessionId?: string
        }
      >
    }
    const meta = view.sessionMeta ?? {}
    for (const raw of view.sessionIds ?? []) {
      const id = String(raw)
      // A shared session can open beside any of the guest's projects.
      if (seen.has(id)) continue
      seen.add(id)
      const entry = meta[id]
      rows.push({
        id,
        title: sessionTitleOf(entry as never) || `Session ${id.slice(0, 8)}`,
        updatedAt: entry?.updatedAt,
        completedAt: entry?.completedAt,
        // WHAT IT IS DOING, when the registry can say (RFC 0284 S8a). Absent
        // rather than idle when it cannot: a session nobody reported on is not
        // a quiet one. Zero hydration, which is why a fill may have it
        // at all.
        ...(registryStatus(entry, now)
          ? { status: registryStatus(entry, now) }
          : {}),
        ...(typeof entry?.statusAt === 'number'
          ? { statusAt: entry.statusAt }
          : {}),
        ...(typeof entry?.sentAt === 'number' ? { sentAt: entry.sentAt } : {}),
        parentSessionId: entry?.parentSessionId
          ? String(entry.parentSessionId)
          : undefined,
        projectId,
        projectName,
      })
    }
  }

  return rows
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
    .slice(0, limit)
}

/** The inbox, live: every project in the World, every session in each. */
export function useWorldSessions(): WorldSessionRow[] {
  const projects = useWorldProjects()
  const projectIds = useMemo(() => projects.map((p) => p.projectId), [projects])
  const docs = useProjectDocs(projectIds)

  return useMemo(
    () =>
      worldSessionRows(
        projects.map((p) => ({
          projectId: p.projectId,
          projectName: p.displayName,
          doc: docs.get(p.projectId),
        })),
      ),
    [projects, docs],
  )
}

/**
 * Every session NOT FILED, across the World, for the lines that say where
 * work is (RFC 0336 S10). Not the inbox: the inbox keeps the newest 200 of
 * everything, so an old question still waiting would fall out behind two
 * hundred newer sessions, filed or not. Filed sessions go first, and nothing
 * else is capped here; the lines bound their own reads.
 *
 * CLAIMS EXPIRE WITH TIME, not only with documents. A worker that dies stops
 * writing, so no document changes, and a projection keyed on documents alone
 * would say "working" forever. Silence is not an event, so the only signal is
 * the clock: ONE timer, set for the moment the soonest run claim would age
 * out. A live worker refreshes its claim first, the documents change, and the
 * timer moves with them, so in the common case it never fires.
 */
export function useWorldActivity(): WorldSessionRow[] {
  const projects = useWorldProjects()
  const projectIds = useMemo(() => projects.map((p) => p.projectId), [projects])
  const docs = useProjectDocs(projectIds)
  const [tick, setTick] = useState(0)
  // When each session was last seen working, across renders, for the hold.
  const lastWorking = useRef<Map<string, number>>(new Map())
  const { rows, nextRelease } = useMemo(() => {
    void tick
    const raw = worldSessionRows(
      projects.map((p) => ({
        projectId: p.projectId,
        projectName: p.displayName,
        doc: docs.get(p.projectId),
      })),
      Number.POSITIVE_INFINITY,
    ).filter((r) => !isSessionDone(r))
    return holdWorking(raw, lastWorking.current, Date.now())
  }, [projects, docs, tick])
  // One timer for whichever comes first: a claim ageing out, or a hold
  // running out. Either changes what the rows say with no document changing.
  const nextExpiry = soonestExpiry(rows)
  const wakeAt =
    nextExpiry === null
      ? nextRelease
      : nextRelease === null
        ? nextExpiry
        : Math.min(nextExpiry, nextRelease)
  useEffect(() => {
    if (wakeAt === null) return
    const wait = Math.max(0, wakeAt - Date.now()) + 50
    const id = setTimeout(() => setTick((n) => n + 1), wait)
    return () => clearTimeout(id)
  }, [wakeAt])
  return rows
}

/**
 * How long a session that was working keeps counting as working after its
 * claim drops. A run ends a step before the next is queued and claimed, and
 * the registry mirrors every one of those transitions, so a busy session reads
 * running, idle, running a few times a minute. Without a hold, every surface
 * counting it ("1 working") blinks on each step.
 */
export const WORKING_HOLD_MS = 8_000

/**
 * Hold "working" through the gaps between a run's steps (fix: the blinking
 * summary). A session seen running or queued keeps that status for
 * `WORKING_HOLD_MS` after its claim turns idle or unknown, so the gap between
 * steps never reads as a stop. A real stop shows that much later. Asking is
 * never held over: a question is news at once. `seen` is updated in place;
 * `nextRelease` is when the soonest hold runs out, to look again then.
 */
export function holdWorking(
  rows: readonly WorldSessionRow[],
  seen: Map<string, number>,
  now: number,
  holdMs = WORKING_HOLD_MS,
): { rows: WorldSessionRow[]; nextRelease: number | null } {
  let nextRelease: number | null = null
  const out = rows.map((r) => {
    if (r.status === 'running' || r.status === 'queued') {
      seen.set(r.id, now)
      return r
    }
    const at = seen.get(r.id)
    if (at === undefined) return r
    if (r.status === 'asking' || now - at >= holdMs) {
      seen.delete(r.id)
      return r
    }
    const release = at + holdMs
    if (nextRelease === null || release < nextRelease) nextRelease = release
    // Without its claim time: the hold, not the claim, decides when this
    // ends, and a stale claim time would wake the expiry timer at once.
    const { statusAt: _claimed, ...held } = r
    return { ...held, status: 'running' as const }
  })
  return { rows: out, nextRelease }
}

/**
 * When the soonest run claim among these rows ages out, or null when none
 * can: only running and queued claims expire (`registryStatus`).
 */
export function soonestExpiry(rows: readonly WorldSessionRow[]): number | null {
  let soonest: number | null = null
  for (const r of rows) {
    if (r.status !== 'running' && r.status !== 'queued') continue
    if (r.statusAt == null) continue
    const at = r.statusAt + STATUS_FRESH_MS
    if (soonest === null || at < soonest) soonest = at
  }
  return soonest
}
